// ═════════════════════════════════════════════════════════════════════════════
//  PAID SICK DAYS — who has used up the year's allowance
// ═════════════════════════════════════════════════════════════════════════════
// BC gives an employee a number of PAID sick days per calendar year. Beyond
// that the absence is still recorded — it is a fact about the worker — but it
// pays nothing: no hours row, no labour cost, 0 hrs on the PDF.
//
// This arithmetic used to sit inside js/app.js between two database calls, and
// it was never exercised. It could not be: from the port until 2026-08-14 the
// sick_days table was admin-only in RLS, and RLS on a SELECT does not raise an
// error — it silently returns FEWER ROWS. So on every timesheet filed by a
// foreman (27 of the last 28) the "days already used" tally came back as zero,
// this rule concluded nobody had used anything, and every sick day was paid no
// matter how many the worker had taken. No error, nothing in a log, nothing on
// screen.
//
// Splitting it out is what lets it be tested at all. The database halves stay
// in js/app.js; what lands here is the decision.

export const DEFAULT_SICK_DAYS_PER_YEAR = 5;

// A sick day on a stat holiday pays the holiday only (time clock rule), so it
// never uses up one of the year's paid sick days (Renan, 2026-10-28).
// statDays = the org's time-clock stat list; callers pass none when the clock
// module is off, so those orgs keep the old count.
export function isStatSickDay(rowOrDate, statDays) {
  const d = typeof rowOrDate === 'string' ? rowOrDate : rowOrDate?.sick_date;
  return Array.isArray(statDays) && !!d && statDays.includes(d);
}

// Marks each entry paid or unpaid, in DATE ORDER, consuming what is left of the
// worker's allowance.
//
//   entries   [{ workerId, date }]      the sick days on THIS timesheet
//   usedByWorker { workerId: number }   days already used this year, NOT
//                                       counting the ones in `entries`
//   allowance  the org's paid days per year
//
// Returns [{ workerId, date, unpaid }] in the order it decided them, which is
// chronological — the earliest days in the week are the ones that get paid,
// because that is the order they happened.
//
// Order matters and is the reason this is a function rather than a filter: two
// workers each with their own remaining balance, several days in one week, and
// a resubmission that must not count itself.
export function markSickDays(entries, usedByWorker = {}, allowance = DEFAULT_SICK_DAYS_PER_YEAR, statDays = []) {
  // Number(null) and Number('') are both 0, so a plain isFinite check would
  // read "the org never set this" as "this org pays nothing" and quietly stop
  // paying every sick day in the company. Only a value that IS a number counts;
  // anything absent or unreadable falls back. An explicit 0 is honoured — an
  // org with no paid sick days is a real thing.
  const n = typeof allowance === 'number' ? allowance : NaN;
  const cap = Number.isFinite(n) && n >= 0 ? n : DEFAULT_SICK_DAYS_PER_YEAR;

  const used = { ...usedByWorker };
  return (entries || [])
    .filter(e => e && e.workerId && e.date)
    .slice()
    .sort((a, b) => String(a.date).localeCompare(String(b.date)))
    .map(e => {
      if (isStatSickDay(e.date, statDays)) return { workerId: e.workerId, date: e.date, unpaid: false, stat: true };
      const already = Number(used[e.workerId]) || 0;
      const unpaid = already + 1 > cap;
      if (!unpaid) used[e.workerId] = already + 1;
      return { workerId: e.workerId, date: e.date, unpaid };
    });
}

// Days already used this year, from the sick_days rows read back for these
// workers — EXCLUDING the dates on the timesheet being filed right now. A
// resubmitted week would otherwise count itself and flip its own days to
// unpaid on the second save. A day that paid nothing (isUnpaidSickDay: over
// the allowance, or hours on another sheet that day) uses no paid day, the
// same count the Sick Days tab shows.
//
//   rows            [{ worker_id, sick_date, portion, note }]
//   submittedDates  { workerId: Set<'YYYY-MM-DD'> }
export function usedByWorker(rows, submittedDates = {}, statDays = []) {
  const out = {};
  (rows || []).forEach(r => {
    if (!r || !r.worker_id) return;
    const mine = submittedDates[r.worker_id];
    if (mine && mine.has(r.sick_date)) return;
    if (isStatSickDay(r, statDays) || isUnpaidSickDay(r)) return;
    // Same trap as the allowance above: Number(null) is 0, which would count a
    // recorded absence as no absence at all and hand the worker a free day. The
    // column is NOT NULL DEFAULT 1, so anything missing or unreadable here is a
    // row that predates a rule or came in sideways — count it as a full day,
    // which is what the record means.
    const portion = typeof r.portion === 'number' ? r.portion : Number(r.portion);
    const usable = Number.isFinite(portion) && portion > 0 ? portion : 1;
    out[r.worker_id] = (out[r.worker_id] || 0) + usable;
  });
  return out;
}

// Approved sick requests (time clock, Time → Time off) count against the
// allowance as soon as they are approved (Renan, 2026-10-28: approved =
// counted), before any timesheet records the day. Each day of `year` (none:
// every year) once per worker, unless a sick_days row already holds that
// worker and date (a signed sheet's row takes the request's place, never both),
// it is a stat holiday, or a sheet holds hours for him that day on any job — he
// worked and was paid, so it was no sick day (a decided request cannot be
// changed, so this is the only way such a day stops counting).
//
//   requests  [{ worker_id, kind, status, days: ['YYYY-MM-DD'] }]
//   sickRows  [{ worker_id, sick_date }]   the sick_days rows, any year
//   hours     [{ worker_id, work_date, regular_hours, overtime_hours }]   the
//             time_entries rows of those days; a row at 0 h pays nothing
//
// Returns [{ worker_id, sick_date, portion: 1, approved: true }], counted like
// a paid day by the Sick Days tab.
export function approvedSickDays(requests, sickRows, year, statDays = [], hours = []) {
  const seen = new Set((sickRows || []).map(r => `${r?.worker_id}|${r?.sick_date}`));
  for (const e of hours || []) if ((Number(e?.regular_hours) || 0) + (Number(e?.overtime_hours) || 0) > 0) seen.add(`${e.worker_id}|${e.work_date}`);
  const out = [];
  for (const t of requests || []) {
    if (!t || t.kind !== 'sick' || t.status !== 'approved' || !t.worker_id) continue;
    for (const d of t.days || []) {
      const key = `${t.worker_id}|${d}`;
      if ((year != null && String(d).slice(0, 4) !== String(year)) || seen.has(key) || isStatSickDay(d, statDays)) continue;
      seen.add(key);
      out.push({ worker_id: t.worker_id, sick_date: d, portion: 1, approved: true });
    }
  }
  return out;
}

// A day the timesheet filed past the allowance: the absence is recorded, but
// nothing was paid — syncTimesheetToDb (js/app.js) writes no hours for it and
// stamps "(UNPAID — …)" into the note. The Sick Days tab counts a worker's
// PAID days against the allowance and lists these apart, so "8 of 5" can no
// longer read as eight paid days when two of them paid nothing.
// ponytail: reads the app's own note marker; a sick_days.unpaid column is the
// upgrade if notes ever start being edited by hand.
export function isUnpaidSickDay(row) {
  return /\(UNPAID\b/.test(String((row && row.note) || ''));
}
